import { Stack } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ResourceSummary } from '../../../common/components/ResourceSummary';
import { ResourceInfoItem } from '../../../common/components/ResourceSummaryInfo';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { KeyCertificatesTable } from '../../../common/keys/components/KeyCertificatesTable';
import { KeyCertificate } from '../../../common/keys/models/KeyCertificate';
import { KeyVariant } from '../../../common/keys/services/KeyCertificatesService';
import { PaginationResult } from '../../../common/types/pagination';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS } from '../../../routing/routes';
import { MatchedKeysTable } from '../components/MatchedKeysTable';
import { OrganizationKeyDetails } from '../models/OrganizationKeyDetails';
import { ReportKey } from '../models/ReportKey';
import { OrganizationKeyService } from '../services/OrganizationKeysService';

type State = BaseState & {
  loading: boolean;
};

type Props = WithRouterProps & {};

class Controller extends BaseController<Props, State> {
  private readonly keyService = new OrganizationKeyService();

  private readonly keyId: number;

  private key: OrganizationKeyDetails | null = null;
  private initialMatchedKeys: PaginationResult<ReportKey> | null = null;
  private initialCertificates: PaginationResult<KeyCertificate> | null = null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ keyId: this.keyId } = this.parseNumericIds(['keyId']));

    this.state = { ...super.getBaseState(), loading: true };
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canAccessOrganizationKey();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.keyService.cancelAllRequests();
  }

  render() {
    const { loading } = this.state;

    const shortDescription = this.key?.getShortDescription() ?? null;
    const hasPrivateKey = this.key?.getHasPrivateKey() ?? null;
    const value = this.key?.getValueJson() ?? null;
    const metadata = this.key?.getMetadata() ?? null;

    const detailsBoxItems: ResourceInfoItem[] = [
      { name: t('keyType'), data: shortDescription },
      { name: t('privateKey'), data: hasPrivateKey ? t('yes') : t('no') },
    ];

    return this.renderBase(
      <SecondaryNav title={t('keyWithId', { keyId: this.keyId })} getBreadcrumbs={this.getBreadcrumbs}>
        <Stack gap={2}>
          <BackButton />
          <ResourceSummary
            title={t('detailsForKeyId', { keyId: this.keyId.toString() })}
            loading={loading}
            items={detailsBoxItems}
            value={value}
            metadataValue={metadata}
            valueTitle={t('keyValue')}
            metadataTitle={t('keyMetadata')}
          />
          {(!this.initialMatchedKeys || this.initialMatchedKeys.data.length > 0) && (
            <MatchedKeysTable keyId={this.keyId} initialData={this.initialMatchedKeys} withPrefill parentLoading={loading} />
          )}
          {(!this.initialCertificates || this.initialCertificates.data.length > 0) && (
            <KeyCertificatesTable
              keyId={this.keyId}
              keyVariant={KeyVariant.OrganizationKey}
              initialData={this.initialCertificates}
              withPrefill
              parentLoading={loading}
            />
          )}
        </Stack>
      </SecondaryNav>
    );
  }

  private async fetchData() {
    try {
      const { key, initialMatchedKeys, initialCertificates } = await this.keyService.getOrganizationKey(this.keyId);
      this.key = key;
      this.initialMatchedKeys = initialMatchedKeys;
      this.initialCertificates = initialCertificates;
      this.setState({ loading: false });
    } catch (e: unknown) {
      this.setState({ loading: false });
      this.handleError(e, { invalidUrl: t('nonExistingKey') });
    }
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getKeyBreadcrumbs({ keyId: this.keyId.toString() });
  }
}

export const OrganizationKeyController = withRouter(Controller);
